import React, { useState } from "react";
import { MagnifyingGlass, Stack } from "@phosphor-icons/react";
import { useGov } from "@/hooks/governance";
import { formatAdaBigCount } from "@/lib/format";
import { Badge, HashCell } from "@/components/primitives";
import { Table } from "@/components/Table";
import {
  GovPageShell, GovCard, GovSection, GovError, GovSkeleton, GovEmpty,
  GovTabs, GovPager, useGovNav,
} from "@/components/governance/GovShell";
import { entityVoteColumns } from "@/components/governance/GovParts";

const PAGE = 25;
const EXAMPLE = "pool1m83drqwlugdt9jn7jkz8hx3pne53acfkd539d9cj8yr92dr4k9y";

const Lookup = ({ meta }) => {
  const navigate = useGovNav();
  const [val, setVal] = useState("");
  const go = () => { const p = val.trim(); if (p) navigate(`/governance/pools/${encodeURIComponent(p)}`); };
  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle="Look up any stake pool's governance votes and voting power.">
      <GovCard className="max-w-2xl">
        <div className="flex items-center gap-2 border border-primary/20 rounded-lg px-3 py-2 bg-card/60">
          <MagnifyingGlass size={15} className="text-muted-foreground" />
          <input
            data-testid="pool-lookup-input"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && go()}
            placeholder="pool1…  (bech32 pool id)"
            className="flex-1 bg-transparent outline-none text-[13px] mono text-foreground placeholder:text-muted-foreground/50"
          />
          <button
            type="button"
            data-testid="pool-lookup-submit"
            onClick={go}
            className="inline-flex items-center gap-1.5 border border-primary/40 bg-primary/10 text-primary px-3 py-1.5 rounded text-[10px] uppercase tracking-[0.15em] mono hover:bg-primary/20 transition-colors"
          >
            Look up
          </button>
        </div>
        <button
          type="button"
          data-testid="pool-example"
          onClick={() => navigate(`/governance/pools/${EXAMPLE}`)}
          className="mt-4 inline-flex items-center gap-2 text-[11px] mono text-muted-foreground hover:text-primary transition-colors"
        >
          <Stack size={13} /> Try an example pool
        </button>
      </GovCard>
    </GovPageShell>
  );
};

const PowerFeed = ({ poolId }) => {
  const { data, error, loading, refetch } = useGov(`/governance/pools/${poolId}/voting_power`);
  if (error) return <GovError error={error} onRetry={refetch} />;
  if (loading && !data) return <GovSkeleton lines={5} />;
  const epochs = Array.isArray(data?.epochs) ? [...data.epochs].reverse() : [];
  return (
    <Table
      columns={[
        { key: "epoch", label: "Epoch", render: (r) => <span className="text-foreground">{r.epoch}</span> },
        { key: "stake", label: "Active Stake", align: "right", render: (r) => (r.data_status === "available" ? `${formatAdaBigCount(r.stake)} ₳` : <span className="text-amber-300/80">{r.unavailable_reason || "unavailable"}</span>) },
        { key: "power", label: "Voting Power", align: "right", render: (r) => (r.data_status === "available" ? <span className="text-foreground">{formatAdaBigCount(r.voting_power)} ₳</span> : "—") },
        { key: "in", label: "In Distribution", align: "right", render: (r) => (r.in_distribution ? <Badge tone="success">yes</Badge> : <Badge>no</Badge>) },
      ]}
      rows={epochs}
      empty="No voting-power history."
    />
  );
};

const Results = ({ meta, poolId }) => {
  const navigate = useGovNav();
  const [tab, setTab] = useState("votes");
  const [page, setPage] = useState(1);
  const votes = useGov(`/governance/pools/${poolId}/votes`, { params: { count: PAGE, page, order: "desc" }, enabled: tab === "votes" });
  const rows = Array.isArray(votes.data) ? votes.data : [];

  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle={poolId}>
      <GovTabs
        tabs={[{ key: "votes", label: "Votes" }, { key: "power", label: "Voting Power" }]}
        active={tab}
        onChange={setTab}
      />
      <div data-testid={`gov-pool-tab-${tab}`}>
        {tab === "votes" ? (
          votes.error ? <GovError error={votes.error} onRetry={votes.refetch} /> : (
            <>
              <Table columns={entityVoteColumns(navigate)} rows={rows} loading={votes.loading} skeletonRows={8} empty="This pool has not cast any governance votes." />
              <GovPager page={page} onPage={setPage} hasNext={rows.length === PAGE} loading={votes.loading} />
            </>
          )
        ) : (
          <PowerFeed poolId={poolId} />
        )}
      </div>
    </GovPageShell>
  );
};

export const PoolVoting = ({ meta, poolId }) =>
  poolId ? <Results meta={meta} poolId={poolId} /> : <Lookup meta={meta} />;
